import { useSyncExternalStore } from 'react'

import { persistedValue } from '@/lib/api/client'

/**
 * The orgs the platform operator opened recently without belonging to them (the platform
 * console's spec, "Opening an org"), for the account menu's Switch to list. Kept in this browser,
 * for the operator who opened them: another person signing in here starts with none.
 */

export type OpenedOrg = { readonly id: string; readonly name: string }

/** How many the Switch to list keeps, the latest first. */
const KEEP = 5

type Stored = { user: string; orgs: OpenedOrg[] }

const stored = persistedValue('damis.opened_orgs')

const NONE: readonly OpenedOrg[] = Object.freeze([])

/** The last list read, so a reader in React gets the same array until the stored value moves. */
let last: { raw: string | null; value: Stored | null } = { raw: null, value: null }

function read(): Stored | null {
  const raw = stored.get()
  if (raw !== last.raw) {
    let value: Stored | null = null
    try {
      value = raw === null ? null : (JSON.parse(raw) as Stored)
    } catch {
      /* not ours, or from an older build: start again */
    }
    last = { raw, value }
  }
  return last.value
}

export const openedOrgs = {
  /** The orgs `userId` opened in this browser, the latest first. */
  list(userId: string): readonly OpenedOrg[] {
    const value = read()
    return value?.user === userId ? value.orgs : NONE
  },
  /** `userId` opened `org`: it goes to the front, once, and the oldest beyond five go. */
  remember(userId: string, org: OpenedOrg): void {
    const earlier = openedOrgs.list(userId).filter((each) => each.id !== org.id)
    const orgs = [{ id: org.id, name: org.name }, ...earlier].slice(0, KEEP)
    stored.set(JSON.stringify({ user: userId, orgs } satisfies Stored))
  },
  subscribe: stored.subscribe,
}

/** The orgs `userId` opened recently, kept in step with the store. */
export function useOpenedOrgs(userId: string): readonly OpenedOrg[] {
  return useSyncExternalStore(openedOrgs.subscribe, () => openedOrgs.list(userId))
}
